import React, { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Calendar } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { apiService } from '../services/api';
import { CreateMaintenanceScheduleDto, UpdateMaintenanceScheduleDto } from '../types';
import MaintenanceScheduleForm from '../components/forms/MaintenanceScheduleForm';

const CreateMaintenanceSchedulePage: React.FC = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [searchParams] = useSearchParams();
	const equipmentId = searchParams.get('equipmentId') ? parseInt(searchParams.get('equipmentId')!) : undefined;
	const [isFormDirty, setIsFormDirty] = useState(false);
	const lastNavigateAttemptRef = useRef<string | null>(null);
	const lastPromptAtRef = useRef<number>(0);
	const suppressNavigationRef = useRef<boolean>(false);

	const createScheduleMutation = useMutation({
		mutationFn: async (data: CreateMaintenanceScheduleDto) => {
			const result = await apiService.createMaintenanceSchedule(data);
			return result;
		},
		onSuccess: (response) => {
			if (response.success && response.data) {
				queryClient.invalidateQueries({ queryKey: ['maintenance-schedules'] });
				toast.success(`Maintenance schedule "${response.data.name}" created successfully!`);
				if (equipmentId) {
					navigate(`/equipment/${equipmentId}`);
				} else {
					navigate('/maintenance-schedules');
				}
			} else {
				toast.error(`Error: ${response.message || 'Unknown error occurred'}`);
			}
		},
		onError: (error: any) => {
			console.error('Error creating schedule:', error);
			const errorMessage = error?.response?.data?.message ||
				error?.response?.data?.errors?.join(', ') ||
				error.message ||
				'Unknown error';
			toast.error(`Error creating schedule: ${errorMessage}`);
		}
	});

	const handleSubmit = (data: CreateMaintenanceScheduleDto | UpdateMaintenanceScheduleDto) => {
		suppressNavigationRef.current = true;
		createScheduleMutation.mutate(data as CreateMaintenanceScheduleDto);
	};

	const handleBackClick = () => {
		if (!isFormDirty) {
			if (equipmentId) {
				navigate(`/equipment/${equipmentId}`);
			} else {
				navigate('/maintenance-schedules');
			}
			return;
		}

		const now = Date.now();
		const targetPath = equipmentId ? `/equipment/${equipmentId}` : '/maintenance-schedules';
		const samePath = lastNavigateAttemptRef.current === targetPath;
		const withinWindow = now - lastPromptAtRef.current < 5000;

		if (samePath && withinWindow) {
			// proceed with navigation
			lastNavigateAttemptRef.current = null;
			lastPromptAtRef.current = 0;
			suppressNavigationRef.current = true;
			navigate(targetPath);
			return;
		}

		lastNavigateAttemptRef.current = targetPath;
		lastPromptAtRef.current = now;
		toast(() => (
			<span className="text-sm">
				You have unsaved changes. Click the back button again to leave.
			</span>
		), { id: 'schedule-back-warning', duration: 4000, icon: '⚠️' });
	};

	return (
		<div className="p-4 space-y-6">
			{/* Header */}
			<div className="mb-2">
				<div className="flex items-center gap-2 mb-2">
					<Calendar className="h-6 w-6 text-gray-900 dark:text-gray-100" />
					<h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Create Maintenance Schedule</h1>
				</div>
				{/* Back Link */}
				<button
					onClick={handleBackClick}
					className="flex items-center gap-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
				>
					<ArrowLeft className="w-4 h-4" />
					<span>{equipmentId ? 'Back to Equipment' : 'Back to Schedules'}</span>
				</button>
			</div>

			<div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
				<MaintenanceScheduleForm
					equipmentId={equipmentId}
					onSubmit={handleSubmit}
					isLoading={createScheduleMutation.isPending}
					error={createScheduleMutation.error ? 'Failed to create schedule' : undefined}
					onFormDirtyChange={setIsFormDirty}
				/>
			</div>
		</div>
	);
};

export default CreateMaintenanceSchedulePage;






